/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { LocalStorageItem } from '@app/constants';
import { Sortable } from '@app/models';
import { parse } from 'date-fns';
import { gsap } from 'gsap';
import { TFunction } from 'i18next';
import chunk from 'lodash/chunk';
import isNil from 'lodash/isNil';
import memoize from 'lodash/memoize';
import omitBy from 'lodash/omitBy';
import times from 'lodash/times';
import uniq from 'lodash/uniq';

export const clean = <T extends object | undefined | null>(obj: T): Partial<T> => {
  return omitBy(obj, isNil) as Partial<T>;
};

export const compareSortable = (first: Sortable, second: Sortable): number => {
  if (!Number.isFinite(first.sortOrder) && !Number.isFinite(second.sortOrder)) {
    return 0;
  }
  if (!Number.isFinite(first.sortOrder) && Number.isFinite(second.sortOrder)) {
    return 1;
  }
  if (Number.isFinite(first.sortOrder) && !Number.isFinite(second.sortOrder)) {
    return -1;
  }
  if (first.sortOrder! < second!.sortOrder!) {
    return 1;
  }
  if (first.sortOrder! > second.sortOrder!) {
    return -1;
  }
  return 0;
};

export const incrementReducer = (state: number): number => {
  return state + 1;
};

export const removeProtocol = (url: string | undefined): string => {
  if (!url) {
    return '';
  }
  return url.replace(/(^\w+:|^)\/\//, '');
};

/** Adds `//` if no protocol specified, if url is already valid, returns it unchanged */
export const repairUrl = (url: string): string => {
  try {
    // eslint-disable-next-line no-new
    new URL(url);
  } catch (error) {
    return `//${url}`;
  }
  return url;
};

export const generateCyrillicAlphabet = (capital = true): string[] => {
  return times(32, (i) => String.fromCharCode(i + (capital ? 1040 : 1072)));
};

export const generateLatinAlphabet = (capital = true): string[] => {
  return times(26, (i) => String.fromCharCode(i + (capital ? 65 : 97)));
};

export const getStubApi = <T>(
  data: T[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ((arg?: { limit?: number; page?: number } | any) => { data: T[]; totalCount: number; isSuccess: boolean }) => {
  const chunkCached = memoize(chunk, (args?: ArrayLike<unknown> | null) => args?.[1]);
  const empty: T[] = [];
  return (arg) => {
    return {
      data: arg?.limit ? chunkCached(data, arg.limit)[Number.isInteger(arg.page) ? arg.page! : 0] ?? empty : data,
      totalCount: data.length,
      isSuccess: true,
    };
  };
};

type ToggleOptions = {
  scale?: boolean;
  duration?: number;
};

export const openEl = (el: Element | string, options?: ToggleOptions): gsap.core.Tween | undefined => {
  if (!el) return undefined;
  return gsap.to(el, {
    height: 'auto',
    scale: options?.scale ? 1 : undefined,
    duration: options?.duration ?? 0.5,
    ease: 'power1.inOut',
  });
};

export const closeEl = (el: Element | string, options?: ToggleOptions): gsap.core.Tween | undefined => {
  if (!el) return undefined;
  return gsap.to(el, {
    height: 0,
    scale: options?.scale ? 0 : undefined,
    duration: options?.duration ?? 0.5,
    ease: 'power1.inOut',
  });
};

export const toggleEl = (el: Element | string, options?: ToggleOptions): gsap.core.Tween | undefined => {
  if (!el) return undefined;
  return gsap.getProperty(el, 'height') === 0 ? openEl(el, options) : closeEl(el, options);
};

export const getSuggestionItems = (): string[] => {
  const suggestionsStr = window.localStorage.getItem(LocalStorageItem.SEARCH_SUGGESTIONS);
  let suggestions: string[] = [];
  try {
    suggestions = suggestionsStr ? (JSON.parse(suggestionsStr) as string[]) : [];
  } catch {
    window.localStorage.setItem(LocalStorageItem.SEARCH_SUGGESTIONS, '[]');
  }
  return suggestions;
};

export const addSuggestionItem = (item: string): void => {
  const items = getSuggestionItems();
  items.unshift(item);
  const updatedItems = uniq(items).slice(0, 5);
  window.localStorage.setItem(LocalStorageItem.SEARCH_SUGGESTIONS, JSON.stringify(updatedItems));
};

export const ymGoal = (name: string): void => {
  window.ym?.(window.ymKey, 'reachGoal', name);
};

export const parseDateWithoutTimezone = (dateStr: string, format: string): string => {
  const parsedDate = parse(dateStr, format, new Date());
  return new Date(Date.UTC(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate())).toISOString();
};

export const toDDMMYYYY = (dateStr: string): string => {
  const date = new Date(dateStr);

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();

  return `${day}.${month}.${year}`;
};

export const getRandomInteger = (min = 0, max = Number.MAX_SAFE_INTEGER): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const isDevMode = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

export const omitUrlPathParam = (path: string, depth = 1): string => {
  let result = path;
  times(depth, () => {
    result = path.replace(/\/?.+\//, '/');
  });
  return result;
};

export const omitUrlPath = (path: string, depth = 1): string => {
  const segments = path.replace(/^\/+|\/+$/g, '').split('/');
  return segments[segments.length - depth] || '';
};

export function formatNumberWithSpaces(numberString: string): string {
  // Удаляем все пробелы и нецифровые символы (кроме минуса и точки)
  const cleaned = numberString.replace(/[^\d-.]/g, '');

  // Разделяем на целую и десятичную части
  const parts = cleaned.split('.');
  let integerPart = parts[0];
  const decimalPart = parts.length > 1 ? `.${parts[1]}` : '';

  // Обрабатываем отрицательные числа
  const hasMinus = integerPart.startsWith('-');
  if (hasMinus) {
    integerPart = integerPart.substring(1);
  }

  // Добавляем пробелы между тысячами
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  // Собираем результат
  return (hasMinus ? '-' : '') + formattedInteger + decimalPart;
}

export function isDateNotInPast(date: Date): boolean {
  if (!(date instanceof Date)) return false; // Проверяем, что это Date

  const today = new Date();

  // Обнуляем часы, минуты, секунды, миллисекунды для точного сравнения по дням
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  return date >= today; // true, если date >= сегодня
}

export function formatDate(date: Date): string {
  const days = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`;
  const month = date.getMonth() > 8 ? date.getMonth() + 1 : `0${date.getMonth() + 1}`;
  return `${days}.${month}.${date.getFullYear()}`;
}
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  const days = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`;
  const month = date.getMonth() > 8 ? date.getMonth() + 1 : `0${date.getMonth() + 1}`;
  const time = dateString.split('T')[1].split(':').slice(0, 2).join(':');
  return `${days}.${month}.${date.getFullYear()}, ${time}`;
}

export function formatDateV2(date: Date): string {
  const days = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`;
  const month = date.getMonth() > 8 ? date.getMonth() + 1 : `0${date.getMonth() + 1}`;
  return `${date.getFullYear()}-${month}-${days}`;
}

export function formatDateV3(date: Date, t: TFunction<'translation', 'month', 'translation'>): string {
  const days = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`;
  const month = t(date.getMonth().toString());
  return `${days} ${month} ${date.getFullYear()}`;
}

export function formatDateTimeV2(dateString: string, t: TFunction<'translation', 'month', 'translation'>): string {
  const date = new Date(dateString);
  const days = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`;
  const month = t(date.getMonth().toString());
  const time = dateString.split('T')[1].split(':').slice(0, 2).join(':');
  return `${days} ${month} ${date.getFullYear()}, ${time}`;
}

export function formatPhoneNumber(phoneNumber: string): string {
  // Проверяем, соответствует ли строка формату "79999999999" (11 цифр, начинается с 7)
  if (/^7\d{10}$/.test(phoneNumber)) {
    // Форматируем номер по шаблону "+7 (999) 999-99-99"
    return `+7 (${phoneNumber.substring(1, 4)}) ${phoneNumber.substring(4, 7)}-${phoneNumber.substring(
      7,
      9,
    )}-${phoneNumber.substring(9)}`;
  }
  // Если формат не соответствует, возвращаем исходную строку
  return phoneNumber;
}
